import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import Layouts from "../../layouts/layouts";
import "react-datepicker/dist/react-datepicker.css";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";

const LiveLocationCell = ({ row }) => {
  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ RESET STATE WHEN ROW CHANGES
  useEffect(() => {
    setAddress(null);
    setLoading(false);
  }, [row._id]);

  const fetchAddress = async () => {
    if (!row.coordinates) {
      setAddress("No coordinates available");
      return;
    }

    const { latitude, longitude } = row.coordinates;

    setLoading(true);

    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_BASE_API}`,
      );

      const data = await res.json();

      if (data.status === "OK" && data.results.length > 0) {
        setAddress(data.results[0].formatted_address);
      } else {
        setAddress("Address not found");
      }
    } catch (err) {
      console.error("Error fetching address:", err);
      setAddress("Error fetching address");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "relative", zIndex: 1 }}>
      {address ? (
        <>
          <span>{address.slice(0, 20)}...</span>

          <FontAwesomeIcon
            icon={faInfoCircle}
            data-tooltip-id={`tooltip-live-location-${row._id}`}
            data-tooltip-content={address}
            style={{ cursor: "pointer", marginLeft: "5px" }}
          />

          <Tooltip
            id={`tooltip-live-location-${row._id}`}
            place="bottom"
            style={{ zIndex: 9999, backgroundColor: "black" }}
          />
        </>
      ) : (
        <button
          onClick={fetchAddress}
          disabled={loading}
          style={{
            padding: "4px 8px",
            background: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "12px",
          }}
        >
          {loading ? "Loading..." : "Click"}
        </button>
      )}
    </div>
  );
};

export default function VehicleDailyHistory() {
  const { id } = useParams();
  const location = useLocation();
  const selectedDate = location.state?.date || null;
  const navigate = useNavigate();

  const [history, setHistory] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [plateNumber, setPlateNumber] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [totalRows, setTotalRows] = useState(0);
  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("HH:mm:ss");
  };

  const fetchHistory = async (from, to, pageNum = 1, limitNum = 5) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const params = {
        page: pageNum,
        limit: limitNum,
      };

      if (from) params.from = moment(from).format("YYYY-MM-DD");
      if (to) params.to = moment(to).format("YYYY-MM-DD");

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/vehicleHistory/vehicle/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        },
      );

      const vehicleHistory = response.data.vehicleHistory || [];

      setTotalRows(response.data.pagination?.totalRecords || 0);

      // const plateNumber = vehicleHistory[0]?.vehicleNumber || "Number Plate";
      if (pageNum === 1 && vehicleHistory.length) {
        setPlateNumber(vehicleHistory[0].vehicleNumber);
      }

      setPlateNumber(plateNumber);
      setHistory(vehicleHistory);
    } catch (error) {
      console.error("Error fetching vehicle history: ", error);
      setHistory([]);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      setFromDate(selectedDate);
      setToDate(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchHistory(fromDate, toDate, page, limit);
    }
  }, [fromDate, toDate, page, limit]);

  const handleMapNavigation = (date) => {
    navigate(`/mymapcomponent/${id}`, {
      state: { date },
    });
  };

  const columns = [
    {
      name: <b>Date</b>,
      selector: (row) => moment(row.lastDate).format("DD-MM-YYYY"),
      sortable: true,
    },
    {
      name: <b>Live Location</b>,
      cell: (row) => <LiveLocationCell row={row} />,
      sortable: true,
    },
    {
      name: <b>Total Km</b>,
      selector: (row) =>
        row.distanceTravel !== undefined && row.distanceTravel !== null
          ? Number(row.distanceTravel).toFixed(2)
          : "N/A",
      sortable: true,
    },
    {
      name: <b>Speed</b>,
      selector: (row) => row.speed || "N/A",
      sortable: true,
    },
    {
      name: <b>Vehicle Status</b>,
      selector: (row) => row.vehicleStatus || "N/A",
      sortable: true,
    },
    {
      name: <b>Battery Percentage</b>,
      selector: (row) => row.battery_percentage || "-",
      sortable: true,
    },
    {
      name: <b>Updated At</b>,
      selector: (row) => row.updatedAt && formatTime(row.updatedAt),
      sortable: true,
    },
  ];

  return (
    <>
      <Layouts />
      <div className="container-fluid">
        <div className="row my-3">
          <div className="col-12">
            <div className="card" style={{ borderTop: "2px solid #4723d9" }}>
              <div className="card-header d-flex justify-content-between align-items-center border-bottom pb-1">
                {plateNumber || "Vehicle History - Daily"}
              </div>
              <div className="card-body">
                <DataTable
                  columns={columns}
                  data={history}
                  pagination
                  fixedHeader
                  highlightOnHover
                  responsive
                  paginationServer
                  paginationTotalRows={totalRows}
                  paginationPerPage={limit}
                  paginationRowsPerPageOptions={[5, 10, 15, 20]}
                  onChangePage={(page) => setPage(page)}
                  onChangeRowsPerPage={(newLimit, page) => {
                    setLimit(newLimit);
                    setPage(page);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
